import { countryCapabilitiesQuery } from "@/api/location";
import {
  ApiError,
  CountryCapabilities,
  GetFormSchemaForMemberRequest,
  GetPayoutDetailsForMemberResponse,
  MoreSessionsRequest,
  MySupporter,
  UpdateBeneficiaryForMemberRequest,
  UpdateFavouriteListingRequest,
  UpdateFavouriteResourceRequest,
  UpdateMyAddressRequest,
  UpdateMyEmailBody,
  UpdateMyLocationRequest,
  UpdateProfileRequest,
} from "@givenwell/marketplace-api";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fallback, InferOutput, object, parse, string } from "valibot";
import { meQueryOptions } from "./auth";
import { marketplaceHomeQueryKey } from "./home";
import { api, auth, queryClient } from "./utils";

export const myExpirationsQuery = queryOptions({
  queryKey: ["my-expirations"],
  queryFn: async () => {
    const response = await api.me.getMyExpirations();
    return response.expirations;
  },
});

export const favouriteListingIdsQuery = queryOptions({
  queryKey: ["favourite-listing-ids"],
  queryFn: async () => {
    const favourites = await api.me.getFavouriteListingIDs();
    return new Set(favourites.listing_ids);
  },
});

export const favouriteResourceIdsQuery = queryOptions({
  queryKey: ["favourite-resource-ids"],
  queryFn: async () => {
    const response = await api.me.getFavouriteResourceIds();
    return new Set(response.resource_ids);
  },
});

export const favouritesQuery = queryOptions({
  queryKey: ["favourites"],
  queryFn: async () => {
    const response = await api.me.getFavourites();
    return response;
  },
});

export const myAddressQuery = queryOptions({
  queryKey: ["me", "address"],
  queryFn: async () => {
    return await api.me.getMyAddress();
  },
});

export const myWalletQueryOptions = queryOptions({
  queryKey: ["me", "wallet"],
  queryFn: async () => {
    const response = await api.me.getMyWallet();
    return response.wallets?.[0] || null;
  },
});

export const myLocationQueryOptions = queryOptions({
  queryKey: ["me", "location"],
  queryFn: async () => {
    const response = await api.me.getMyLocation();
    return response.location || null;
  },
});

export const mySupportersQuery = () =>
  queryOptions({
    queryKey: ["my-supporters"],
    queryFn: async () => {
      try {
        return await api.me.getMySupporters();
      } catch (e) {
        const error = e as unknown as ApiError;
        if (error.status === 400) {
          const response = {
            supporters: [],
          } satisfies Awaited<ReturnType<typeof api.me.getMySupporters>>;
          return response;
        } else {
          throw error;
        }
      }
    },
  });

export type EapFeature = CountryCapabilities["eap_features"][number];

/** How strongly to warn the member about employer-inferability before an EAP booking/claim. */
export type EapPrivacyWarning = NonNullable<MySupporter["eap"]>["eap_privacy_warning"];

/**
 * Resolves the privacy-warning tier for the EAP supporter a booking/claim will be charged against.
 * Returns "none" when there is no matching EAP supporter (nothing to warn about).
 */
export function getEapPrivacyWarning(
  supporters: MySupporter[] | undefined,
  supporterId: string | null | undefined,
): EapPrivacyWarning {
  if (!supporterId) return "none";
  const supporter = supporters?.find(s => s.supporter_id === supporterId);
  return supporter?.eap?.eap_privacy_warning ?? "none";
}

export type MemberFeatures = {
  hasSupporters: boolean;
  eapSupporter: MySupporter | null;

  hasAllowance: boolean;
  hasAllowanceMarketplace: boolean;

  hasEap: boolean;
  hasEapMarketplace: boolean;

  hasClaims: boolean;
};

/**
 * Runs the smallest possible marketplace search to find out whether the member's location has any
 * listings at all. If the search itself fails we assume there are listings rather than hiding the
 * marketplace on a transient error.
 */
async function hasAnyMarketplaceListings(): Promise<boolean> {
  try {
    const results = await api.listingSearch.searchForListings({
      requestBody: {
        search_text: null,
        category_id: [],
        variant_types: [],
        listing_type: "marketplace",
        next_token: { token: null, page_size: 1 },
      },
    });
    return results.total_items > 0 || results.items.length > 0;
  } catch {
    return true;
  }
}

async function getMemberFeatures() {
  const [supporters, location, hasListings] = await Promise.all([
    queryClient.ensureQueryData(mySupportersQuery()),
    queryClient.ensureQueryData(myLocationQueryOptions),
    hasAnyMarketplaceListings(),
  ]);
  const capabilities = await queryClient.ensureQueryData(countryCapabilitiesQuery(location?.country_code));

  // Even when the country allows the marketplace, only surface it if there is actually something to
  // show the member.
  const countryAllowsMarketplace = capabilities.marketplace_allowed && hasListings;
  const countryAllowsEapClaims = capabilities.eap_allowed && capabilities.eap_features?.includes("claims");
  const supporterHasEap = supporters.supporters.some(supporter => !!supporter.eap);
  const hasEap = !!supporterHasEap;

  // If someone has no eap AND no allowance, we'll let them see the marketplace
  const hasAllowance = supporters.supporters.some(s => s.has_allowance !== false) || !hasEap;

  const hasSupporters = (supporters.supporters.length || 0) > 0;

  const features: MemberFeatures = {
    hasSupporters,
    eapSupporter: supporters.supporters?.find(s => s.eap !== null) ?? null,

    hasAllowance,
    hasAllowanceMarketplace: countryAllowsMarketplace && hasAllowance,

    hasEap: hasEap,
    hasEapMarketplace: countryAllowsMarketplace && hasEap,

    hasClaims: hasAllowance || countryAllowsEapClaims,
  };

  return features;
}

const defaultMemberFeatures: MemberFeatures = {
  eapSupporter: null,
  hasSupporters: true,

  hasAllowance: true,
  hasAllowanceMarketplace: true,

  hasEap: false,
  hasEapMarketplace: true,
  hasClaims: true,
};

export const memberFeaturesQuery = queryOptions({
  queryKey: ["member-features"],
  queryFn: getMemberFeatures,
});

export function useMemberFeatures(): MemberFeatures {
  return useQuery(memberFeaturesQuery).data || defaultMemberFeatures;
}

// -------------------------------------------------------------------------------------------------

export function useRequestMoreSessionsMutation() {
  return useMutation({
    mutationFn: async (requestBody: MoreSessionsRequest) => {
      return await api.me.requestMoreSessions({ requestBody });
    },
  });
}

export function useUpdateFavouriteListingMutation() {
  return useMutation({
    mutationFn: async (args: UpdateFavouriteListingRequest) => {
      await api.me.updateFavouriteListing({ requestBody: args });
    },
    onMutate(variables) {
      queryClient.setQueryData(favouriteListingIdsQuery.queryKey, oldData => {
        if (!oldData) return oldData;
        const newSet = new Set(oldData);
        if (variables.is_favourite) {
          newSet.add(variables.listing_id);
        } else {
          newSet.delete(variables.listing_id);
        }
        return newSet;
      });
    },
    onSuccess() {
      queryClient.invalidateQueries(favouriteListingIdsQuery);
      queryClient.invalidateQueries(favouritesQuery);
    },
    onError(_error, variables) {
      queryClient.setQueryData(favouriteListingIdsQuery.queryKey, oldData => {
        if (!oldData) return oldData;
        const newSet = new Set(oldData);
        if (variables.is_favourite) {
          newSet.delete(variables.listing_id);
        } else {
          newSet.add(variables.listing_id);
        }
        return newSet;
      });
    },
  });
}

export function useUpdateFavouriteResourceMutation() {
  return useMutation({
    mutationFn: async (args: UpdateFavouriteResourceRequest) => {
      await api.me.updateFavouriteResource({ requestBody: args });
    },
    onMutate(variables) {
      queryClient.setQueryData(favouriteResourceIdsQuery.queryKey, oldData => {
        if (!oldData) return oldData;
        const newSet = new Set(oldData);
        if (variables.is_favourite) {
          newSet.add(variables.resource_id);
        } else {
          newSet.delete(variables.resource_id);
        }
        return newSet;
      });
    },
    onSuccess() {
      queryClient.invalidateQueries(favouriteResourceIdsQuery);
      queryClient.invalidateQueries(favouritesQuery);
    },
    onError(_error, variables) {
      queryClient.setQueryData(favouriteResourceIdsQuery.queryKey, oldData => {
        if (!oldData) return oldData;
        const newSet = new Set(oldData);
        if (variables.is_favourite) {
          newSet.delete(variables.resource_id);
        } else {
          newSet.add(variables.resource_id);
        }
        return newSet;
      });
    },
  });
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: Omit<UpdateProfileRequest, "refresh_token">) => {
      const refreshToken = auth.getRefreshToken();
      const result = await api.me.updateMyProfile({
        requestBody: { ...args, refresh_token: refreshToken ?? undefined },
      });
      if (result.access_token) {
        auth.saveAuthTokens({ access_token: result.access_token, refresh_token: result.refresh_token ?? undefined });
      }
    },
    onSuccess(_, variables) {
      queryClient.setQueryData(meQueryOptions.queryKey, prev => {
        if (!prev) return prev;
        return {
          ...prev,
          given_name: variables.given_name ?? prev.given_name,
          family_name: variables.family_name ?? prev.family_name,
        };
      });
      queryClient.invalidateQueries(meQueryOptions);
    },
  });
}

export function useChangeEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: Omit<UpdateMyEmailBody, "refresh_token">) => {
      const refreshToken = auth.getRefreshToken();
      const result = await api.me.updateMyEmail({ requestBody: { ...args, refresh_token: refreshToken } });
      if (result.access_token) {
        auth.saveAuthTokens({ access_token: result.access_token, refresh_token: result.refresh_token ?? undefined });
      }
    },
    onSuccess(_, variables) {
      queryClient.setQueryData(meQueryOptions.queryKey, prev => {
        if (!prev) return prev;
        return {
          ...prev,
          email: variables.new_email_address,
        };
      });
      queryClient.invalidateQueries(meQueryOptions);
    },
  });
}

export function useUpdateMyAddressMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateMyAddressRequest) => {
      return await api.me.updateMyAddress({ requestBody: body });
    },
    onSuccess: () => {
      client.invalidateQueries(myAddressQuery);
    },
  });
}

export function useUpdateMyLocationMutation() {
  return useMutation({
    mutationFn: async (req: UpdateMyLocationRequest) => {
      await api.me.updateMyLocation({ requestBody: req });
    },
    onSettled() {
      queryClient.resetQueries({
        queryKey: marketplaceHomeQueryKey,
      });
      queryClient.invalidateQueries();
    },
  });
}

export const payoutDetailsForMemberQuery = queryOptions({
  queryKey: ["me", "payout-details"],
  queryFn: async () => {
    const user = await queryClient.ensureQueryData(meQueryOptions);

    return await api.me.getPayoutDetailsForMember({ memberId: user.id });
  },
});

export const memberBeneficiaryQuery = queryOptions({
  queryKey: ["me", "beneficiary"],
  queryFn: async () => {
    const user = await queryClient.ensureQueryData(meQueryOptions);
    return await api.me.getBeneficiaryForMember({ memberId: user.id });
  },
});

type SimplifiedGetFormSchemaForMemberRequest = Omit<GetFormSchemaForMemberRequest, "member_id">;
export const formSchemaForMemberQuery = (params: SimplifiedGetFormSchemaForMemberRequest) =>
  queryOptions({
    queryKey: ["me", "form-schema", params],
    queryFn: async () => {
      const user = await queryClient.ensureQueryData(meQueryOptions);
      let schema = await api.me.getFormSchemaForMember({
        requestBody: {
          ...params,
          member_id: user.id,
        },
      });
      // force a local clearing schema if none provided
      if (!params.local_clearing_system) {
        const clearingSystemField = schema.fields.find(
          field => field.path === "beneficiary.bank_details.local_clearing_system",
        );
        const defaultValue = clearingSystemField?.field.default;
        if (defaultValue) {
          const newParams = {
            ...params,
            local_clearing_system: defaultValue,
          };
          schema = await api.me.getFormSchemaForMember({
            requestBody: {
              ...newParams,
              member_id: user.id,
            },
          });
          queryClient.setQueryData(formSchemaForMemberQuery(newParams).queryKey, () => schema);
        }
      }
      return schema;
    },
  });

/**
 * Resolves a beneficiary form's dynamic (DYNAMIC_SELECT) options — e.g. the China
 * bank/branch (CNAPS) selector — via the AirWallex "supported financial institutions"
 * lookup. Searched by keyword; `truncated` indicates the caller should refine it.
 */
export async function searchFinancialInstitutionsForMember(params: {
  bank_country?: GetFormSchemaForMemberRequest["bank_country"];
  keyword: string;
  bank_identifier?: string;
}) {
  const user = await queryClient.ensureQueryData(meQueryOptions);
  return api.me.getFinancialInstitutionsForMember({
    requestBody: {
      ...params,
      member_id: user.id,
    },
  });
}

/**
 * Mints a short-lived (30s, single-use) AirWallex authorization code for the embedded
 * beneficiary form. Must be called immediately before initialising the Components SDK — hence
 * a mutation rather than a cached query. The scope is fixed server-side; the frontend can't
 * request it.
 */
export function useAuthorizeAirWallexComponentMutation() {
  return useMutation({
    mutationFn: () => api.airWallexComponents.authorizeAirWallexComponent(),
    // This only fetches an auth token; it shouldn't invalidate cached data.
    onSuccess: undefined,
  });
}

/** The currencies the beneficiary-requirements endpoint accepts, per the generated client. */
type PayerCurrency = NonNullable<
  Parameters<typeof api.airWallexComponents.getBeneficiaryRequirements>[0]["payerCurrency"]
>;

/**
 * Country-specific values the embedded beneficiary form needs to render the bank fields directly
 * (account currency + default local clearing system), so it can skip the transfer-method picker.
 *
 * `payerCurrency` is the currency the member's payouts are funded in (their wallet currency). It is
 * required for a country with no account currency of its own — Cambodia is paid in whatever currency
 * funds it, because Airwallex cannot settle KHR — and safe to send for the rest, which accept it when
 * it matches their fixed currency and reject it when it disagrees.
 */
export const beneficiaryRequirementsQuery = (bankCountry: string, payerCurrency: PayerCurrency | undefined) =>
  queryOptions({
    queryKey: ["me", "beneficiary-requirements", bankCountry, payerCurrency],
    queryFn: () => api.airWallexComponents.getBeneficiaryRequirements({ bankCountry, payerCurrency }),
    staleTime: Infinity,
    // Without the payer currency a payer-dependent country would 400; wait until we've loaded it.
    enabled: payerCurrency !== undefined,
  });

type SimplifiedUpdateBeneficiaryForMemberRequest = Omit<UpdateBeneficiaryForMemberRequest, "member_id">;

export function useUpdateBeneficiaryForMemberMutation() {
  return useMutation({
    mutationFn: async (requestBody: SimplifiedUpdateBeneficiaryForMemberRequest) => {
      const user = await queryClient.ensureQueryData(meQueryOptions);
      return await api.me.updateBeneficiaryForMember({
        requestBody: {
          ...requestBody,
          member_id: user.id,
        },
      });
    },
  });
}

export type PayoutTiming = GetPayoutDetailsForMemberResponse["payout_timing"];

export function useUpdatePayoutTimingMutation() {
  return useMutation({
    mutationFn: async (payoutTiming: PayoutTiming) => {
      const user = await queryClient.ensureQueryData(meQueryOptions);
      api.me.updatePayoutTiming({
        requestBody: {
          member_id: user.id,
          payout_timing: payoutTiming,
        },
      });
    },
  });
}

const UserStateSchema = fallback(
  object({
    // The release date of the most recent "What's New" notice that the user has seen.
    lastSeenUpdateNotice: fallback(string(), "1970-01-01"),
    // Whether the user has seen the token revaluation dialog
    seenRevaluation: fallback(string(), ""),
  }),
  { lastSeenUpdateNotice: "1970-01-01", seenRevaluation: "" },
);
export type UserState = InferOutput<typeof UserStateSchema>;

export const myStateQuery = queryOptions({
  queryKey: ["me", "state"],
  queryFn: async () => {
    const user = await queryClient.ensureQueryData(meQueryOptions);
    const response = await api.me.getMyState({
      memberId: user.id,
    });
    return parse(UserStateSchema, response);
  },
});

export function useUpdateMyStateMutation() {
  return useMutation({
    mutationFn: async (state: UserState) => {
      const user = await queryClient.ensureQueryData(meQueryOptions);
      return await api.me.updateMyState({
        requestBody: {
          member_id: user.id,
          state: state,
        },
      });
    },
    onMutate(variables) {
      queryClient.setQueryData(myStateQuery.queryKey, () => variables);
    },
    onSuccess() {
      queryClient.invalidateQueries(myStateQuery);
    },
  });
}
